/**
 * GlobalsListPage — List all CMS globals with links to their editors.
 *
 * Route: /cms/globals
 *
 * Shows 4 globals with human-readable names.
 * Each card links to /cms/globals/:slug for editing.
 */

import { Link } from 'react-router-dom'
import { useGlobals } from '@/hooks/useCms'

// ── Human-readable names for each global slug ─────────────────────────────────

const GLOBAL_DISPLAY_NAMES: Record<string, { name: string; description: string }> = {
  'site-texts': {
    name: 'Textos del Sitio',
    description: 'Hero, Problema/Solución, CTA final, Trusted By, Testimonios, Integraciones',
  },
  'social-proof': {
    name: 'Social Proof',
    description: 'Métricas (restaurantes, órdenes, uptime, satisfacción) y badge de confianza',
  },
  'contact-settings': {
    name: 'Configuración de Contacto',
    description: 'Email, teléfono, redes sociales y horarios de atención',
  },
  'hub-standalone': {
    name: 'HUB Standalone',
    description: 'Título, descripción y niveles de precio del HUB',
  },
  'virtual-queue': {
    name: 'Fila Virtual',
    description: 'Textos de la sección Fila Virtual: headlines, phone screen, stats y capabilities',
  },
  'features-showcase': {
    name: 'Features Showcase',
    description: 'Títulos y descripciones de todas las cards de features del landing',
  },
  'pricing-texts': {
    name: 'Textos de Pricing',
    description: 'Labels de planes, descripciones, CTAs y badges de pricing',
  },
  'navigation': {
    name: 'Navegación',
    description: 'Header nav links y Footer textos (descripción, columnas, legal)',
  },
}

// ─── Component ────────────────────────────────────────────────────────────────

export function GlobalsListPage() {
  const { data: globals, isLoading, error } = useGlobals()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-gray-500">Cargando globales...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-700">Error al cargar los globales: {error.message}</p>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">CMS — Contenido Global</h1>
        <p className="mt-1 text-gray-500">
          Edita los textos y configuraciones de la landing page por idioma.
        </p>
      </div>

      <div className="space-y-3">
        {globals?.map((global) => {
          const display = GLOBAL_DISPLAY_NAMES[global.slug] ?? {
            name: global.slug,
            description: 'Sin descripción',
          }

          return (
            <Link
              key={global.id}
              to={`/cms/globals/${global.slug}`}
              className="block bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-400 hover:shadow-sm transition-all"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-medium text-gray-900">{display.name}</h2>
                  <p className="text-sm text-gray-500 mt-0.5">{display.description}</p>
                  <p className="text-xs text-gray-400 mt-1 font-mono">{global.slug}</p>
                </div>
                <svg
                  className="w-5 h-5 text-gray-400 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Link>
          )
        })}

        {(!globals || globals.length === 0) && (
          <div className="text-center py-12 text-gray-500">
            No se encontraron globales. El seed debería haberlos creado automáticamente.
          </div>
        )}
      </div>
    </div>
  )
}
